/**
 * Lens card anchors — where the persona card sits on each reference screen.
 *
 * Pure TypeScript (no JSX, no DOM) so tests can enumerate the table. Every
 * anchor was measured against the canonical PNG: the rect it describes
 * contains no painted text or primary control (MOTION-SPEC §8 lists the free
 * area each one uses). Percentages are canvas percentages; `lensSlotStyle()`
 * converts them into the shell's 1672 / 941 viewport box when the scene canvas
 * is narrower (classroom), so the card lands on the same painted pixels
 * whichever element the shell makes its offset parent.
 */

import type { CSSProperties } from 'react';
import type { ReferenceSceneId } from '../../reference/types';
import { SCENE_CANVAS } from '../types.ts';

/** How much of the card is shown at rest; any density expands to `full` on demand. */
export type LensDensity = 'full' | 'compact' | 'strip' | 'pill';

export interface LensAnchorSpec {
  /** Horizontal edge the card hangs from and its inset, % of canvas width. */
  side: 'left' | 'right';
  x: number;
  /** Vertical edge the card hangs from and its inset, % of canvas height. A card grows away from this edge when it expands. */
  edge: 'top' | 'bottom';
  y: number;
  /** Card width at rest, % of canvas width (never above 34). */
  width: number;
  density: LensDensity;
}

/** Legacy corner name, derived from the spec; kept for callers that only need the quadrant. */
export type LensAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const LENS_MAX_WIDTH = 34;
/** Width the card takes when a smaller density expands to `full`, % of canvas width. */
export const LENS_EXPANDED_MIN_WIDTH = 30;

export const LENS_ANCHOR: Record<ReferenceSceneId, LensAnchorSpec> = {
  // Under the painted "9Б · Физика" control (ends y 66), above the "3. Виктория" label (y 185): the
  // bottom-left strip of the spec sits exactly under the dock's band whenever the dock is shown.
  classroom: { side: 'right', x: 3.4, edge: 'top', y: 8, width: 16, density: 'pill' },
  // Left of the title, between the logo (ends y 67) and pill 1 (starts y 238).
  motivation: { side: 'left', x: 1.4, edge: 'top', y: 7.6, width: 20, density: 'compact' },
  // Dark lab header; must end above the "Паттерн рекомендаций" compass (y ≈ 170).
  'teacher-goal': { side: 'right', x: 2, edge: 'top', y: 2.6, width: 23, density: 'compact' },
  // Gap between the layer list and the mastery legend; expands upward over the list.
  'knowledge-map': { side: 'left', x: 1.6, edge: 'bottom', y: 34, width: 13.5, density: 'pill' },
  // Under the title's second half (title ends y 77), right of the subtitle (ends x 928), above the lock labels (y 140).
  'personal-route': { side: 'left', x: 57.5, edge: 'top', y: 9.6, width: 22, density: 'pill' },
  // Faint history rings only; the 8th step label starts at x 345.
  'learning-cycle': { side: 'left', x: 1.8, edge: 'top', y: 26, width: 18.5, density: 'full' },
  // Between the tutor card (ends y ≈ 452) and the "Пустые баллы" panel; the dashed path underneath is
  // decorative. Hung from the bottom so the expanded card grows up over the tutor card, not into the dock band.
  agency: { side: 'right', x: 4.3, edge: 'bottom', y: 35.8, width: 21, density: 'compact' },
  // Under the "5 Route Update" header, above "Updated Route".
  'teacher-workspace': { side: 'right', x: 1.4, edge: 'top', y: 16.4, width: 16, density: 'pill' },
  // Between the depth pills (end y 159) and the research panel (starts y ≈ 284).
  technology: { side: 'left', x: 64, edge: 'top', y: 17.2, width: 32, density: 'strip' },
  // Above the headline (starts y ≈ 140).
  finale: { side: 'left', x: 4.8, edge: 'top', y: 2.4, width: 34, density: 'strip' },
};

export function lensCorner(scene: ReferenceSceneId): LensAnchor {
  const anchor = LENS_ANCHOR[scene];
  return `${anchor.edge}-${anchor.side}`;
}

/** Aspect of the shell viewport; every scene canvas is letterboxed inside it (MOTION-SPEC §2). */
export const VIEWPORT_ASPECT = 1672 / 941;

/** Which box the card's offset parent is: the constant viewport, or the scene canvas itself. */
export type LensFrame = 'viewport' | 'canvas';

/** Width the card takes once expanded to `full`, % of canvas width. */
export function lensExpandedWidth(scene: ReferenceSceneId): number {
  return Math.min(LENS_MAX_WIDTH, Math.max(LENS_ANCHOR[scene].width, LENS_EXPANDED_MIN_WIDTH));
}

/**
 * Inline style for the slot: absolute offsets in percent of the offset parent
 * plus `--sn-lens-w` (the rest width in canvas %) so the stylesheet can derive
 * the canvas unit from the slot's own inline size (`100cqi / --sn-lens-w`).
 */
export function lensSlotStyle(scene: ReferenceSceneId, frame: LensFrame = 'viewport'): CSSProperties {
  const anchor = LENS_ANCHOR[scene];
  const canvas = SCENE_CANVAS[scene];
  const canvasAspect = canvas.width / canvas.height;
  // Canvas height always fills the viewport; a narrower canvas is centred horizontally.
  const scale = frame === 'viewport' ? Math.min(1, canvasAspect / VIEWPORT_ASPECT) : 1;
  const offset = (1 - scale) * 50;
  const horizontal = `${round(offset + anchor.x * scale)}%`;
  const style: Record<string, string> = {
    width: `${round(anchor.width * scale)}%`,
    '--sn-lens-w': String(anchor.width),
    '--sn-lens-wx': String(lensExpandedWidth(scene)),
  };
  style[anchor.side] = horizontal;
  style[anchor.edge] = `${anchor.y}%`;
  return style as CSSProperties;
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
